import React from 'react';
import {
  Box,
  Checkbox,
  IconButtonProps,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DocumentTableRow from './DocumentTableRow';
import { useNavigate, useLocation } from 'react-router';

import { IDocumentUnscoped } from '../../types/my-types';
import { DOCUMENT_DATA_STATUS_TOOLTIP } from '../../constants';
import { compactTableStyles } from '../common/tableStyles';
import { syncSelectedDocumentParams } from './documentUtils';

interface DocumentTableProps {
  documents: IDocumentUnscoped[];
  loading: boolean;
  previousPageDoc: () => void;
  nextPageDoc: () => void;
  totalDocuments: number;
  page: number;
  perPage: number;
}

const DocumentTable = ({
  documents,
  loading,
  previousPageDoc,
  nextPageDoc,
  totalDocuments,
  page,
  perPage,
}: DocumentTableProps) => {
  const navigate = useNavigate();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const selectedIds = searchParams.get('selectedIds')?.split(',') || [];

  const areVisibleDocumentsAllSelected =
    documents.length > 0 && selectedIds.length > 0
      ? documents.every((document: any) => selectedIds.includes(document.id))
      : false;

  return (
    <>
      <Box id="extraction-batch-list" sx={[compactTableStyles.containerSx, { overflowX: 'auto' }]}>
        <Table sx={[compactTableStyles.tableSx, { minWidth: '1500px' }]}>
          <TableHead sx={compactTableStyles.tableHeadSx}>
            <TableRow>
              {/* Expand column */}
              <TableCell align="center" sx={{ width: '40px', px: '4px !important' }}>
                {/* Empty - expand buttons in rows */}
              </TableCell>

              {/* Checkbox */}
              <TableCell sx={{ width: '40px', px: '4px !important', py: '0 !important' }}>
                <Checkbox
                  size="small"
                  inputProps={
                    {
                      'aria-label': 'secondary checkbox',
                      'data-cy': `checkbox-manifest-all-table-rows`,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  checked={areVisibleDocumentsAllSelected}
                  onChange={() => {
                    const currentParams = new URLSearchParams(window.location.search);
                    const currentSelectedIds = currentParams.get('selectedIds')?.split(',') || [];
                    const visibleIds = documents.map((document: any) => document.id);
                    if (!areVisibleDocumentsAllSelected) {
                      const newSelectedIds = Array.from(
                        new Set([...visibleIds, ...currentSelectedIds])
                      );
                      syncSelectedDocumentParams(currentParams, newSelectedIds);
                      navigate({
                        search: currentParams.toString(),
                      });
                    } else {
                      const newSelectedIds = currentSelectedIds.filter(
                        (id) => !visibleIds.includes(id)
                      );
                      syncSelectedDocumentParams(currentParams, newSelectedIds);

                      navigate({
                        search: currentParams.toString(),
                      });
                    }
                  }}
                  sx={{ color: '#65BBA9', '&.Mui-checked': { color: '#65BBA9' } }}
                />
              </TableCell>

              <TableCell align="left">ID</TableCell>
              <TableCell align="left">Split parent</TableCell>
              <TableCell align="center">Children</TableCell>
              <TableCell align="left">Vendor</TableCell>
              <TableCell align="left">Name</TableCell>
              <TableCell align="center" sx={{ minWidth: '100px' }}>
                Date
              </TableCell>
              <TableCell align="center" sx={{ width: '60px' }}>
                Pages
              </TableCell>

              <TableCell align="center" sx={{ width: '170px', maxWidth: '170px' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
                >
                  Data Status
                  <Tooltip title={DOCUMENT_DATA_STATUS_TOOLTIP} placement="top">
                    <HelpOutlineIcon
                      sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', cursor: 'help' }}
                    />
                  </Tooltip>
                </Box>
              </TableCell>

              <TableCell align="left" sx={{ minWidth: '330px', width: '330px' }}>
                Info
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  minWidth: '190px',
                  width: '190px',
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                  backgroundColor: '#112233',
                  boxShadow: '-6px 0 8px -8px rgba(0,0,0,0.35)',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-cy="tablebody-document-manifest" sx={compactTableStyles.tableBodySx}>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i} sx={{ height: 49 }}>
                    <TableCell align="center" sx={{ width: '40px', px: '4px !important' }} />
                    <TableCell align="center" sx={{ width: '40px', px: '4px !important' }} />
                    <TableCell>
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={14}
                        width={72}
                        sx={{ borderRadius: '4px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={14}
                        width={48}
                        sx={{ borderRadius: '4px' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={14}
                        width={24}
                        sx={{ borderRadius: '4px', mx: 'auto' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={14}
                        width={[80, 110, 64, 96, 88][i]}
                        sx={{ borderRadius: '4px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={14}
                        width={[140, 100, 160, 120, 130][i]}
                        sx={{ borderRadius: '4px' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={14}
                        width={72}
                        sx={{ borderRadius: '4px', mx: 'auto' }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ width: '60px' }}>
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={14}
                        width={20}
                        sx={{ borderRadius: '4px', mx: 'auto' }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ width: '170px' }}>
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={22}
                        width={80}
                        sx={{ borderRadius: '6px', mx: 'auto' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={20}
                        width={72}
                        sx={{ borderRadius: '10px' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                        <Skeleton animation="wave" variant="circular" width={18} height={18} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              : null}
            {!loading && documents.length > 0
              ? documents?.map((document: any) => (
                  <DocumentTableRow key={document.id} document={document} />
                ))
              : null}
            {!loading && documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12}>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                    No documents found.
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        {totalDocuments > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 150]}
            component="div"
            count={totalDocuments}
            rowsPerPage={perPage}
            page={page}
            backIconButtonProps={
              {
                'aria-label': 'Previous Page',
                'data-cy': 'btn-prev-page',
              } as IconButtonProps & { 'data-cy': string }
            }
            nextIconButtonProps={
              {
                'aria-label': 'Next Page',
                'data-cy': 'btn-next-page',
              } as IconButtonProps & { 'data-cy': string }
            }
            onPageChange={(e, p) => {
              e?.preventDefault();
              if (p > page) {
                nextPageDoc();
              } else {
                previousPageDoc();
              }

              searchParams.set('page', p.toString());
              navigate({
                search: searchParams.toString(),
              });
            }}
            onRowsPerPageChange={(e: any) => {
              searchParams.set('perPage', e.target.value);
              navigate({
                search: searchParams.toString(),
              });
            }}
          />
        )}
      </Box>
    </>
  );
};

export default DocumentTable;
